import { create } from 'zustand';

import type { UserRef } from '@/shared/api/types';

import type { DragGoal, MapViewer } from '../api';

/** This tab (presence entries are per tab, so two tabs of one person are two entries). */
export const CLIENT_ID: string =
  typeof crypto !== 'undefined' && 'randomUUID' in crypto
    ? crypto.randomUUID()
    : `tab-${Math.random().toString(36).slice(2)}-${Date.now().toString(36)}`;

/** A remote drag older than this is dropped (its "done" may have been lost). */
export const DRAG_STALE_MS = 2500;

/**
 * A dropped goal keeps its preview at the drop place this long at most, until the saved move reaches this tab (the map
 * refetched at the move's revision) — so it never jumps back to its old place in between.
 */
export const DROP_STALE_MS = 6000;

export interface RemoteDrag extends DragGoal {
  userId: string;
  clientId: string;
  at: number;
  /** The drag ended (dropped): the preview waits for the saved move. */
  done?: boolean;
  /** The saved move's revision: the preview goes once the map shown is at least this new. */
  untilRevision?: number;
}

/** What this tab focuses on a map: the goal whose card is open (panel) or whose title it edits (canvas). */
export interface Focus {
  goalId: string | null;
  editing: boolean;
}

interface PresenceState {
  /** Everybody on a map now (from heartbeats and `goals.presence-changed`), by map. */
  viewers: Record<string, MapViewer[]>;
  /** Goals other editors are dragging right now, by map and goal. */
  drags: Record<string, Record<string, RemoteDrag>>;
  /** This tab's focus per source, by map. */
  focus: Record<string, { panel?: Focus; canvas?: Focus }>;
  setViewers: (mapId: string, viewers: MapViewer[]) => void;
  applyDrag: (mapId: string, userId: string, clientId: string, goals: DragGoal[], done: boolean) => void;
  clearDrags: (mapId: string, predicate: (drag: RemoteDrag) => boolean) => void;
  /** The person saved a change (`goals.map-changed`): their previews go once the map is at `revision`. */
  settleDrags: (mapId: string, userId: string, revision: number) => void;
  setFocus: (mapId: string, source: 'panel' | 'canvas', focus: Focus | null) => void;
}

export const usePresenceStore = create<PresenceState>()((set) => ({
  viewers: {},
  drags: {},
  focus: {},
  setViewers: (mapId, viewers) => set((s) => ({ viewers: { ...s.viewers, [mapId]: viewers } })),
  applyDrag: (mapId, userId, clientId, goals, done) =>
    set((s) => {
      if (clientId === CLIENT_ID) return s;
      const current = new Map(Object.entries(s.drags[mapId] ?? {}));
      const now = Date.now();
      for (const goal of goals) {
        // A saved move may have been announced before the drop message: its revision still ends the preview.
        const untilRevision = current.get(goal.goalId)?.untilRevision;
        current.set(goal.goalId, {
          ...goal,
          userId,
          clientId,
          at: now,
          done,
          ...(untilRevision ? { untilRevision } : {}),
        });
      }
      if (done) {
        // The drop: every preview of that tab stays at its last place until the saved move arrives (or goes stale).
        for (const [goalId, drag] of current)
          if (drag.clientId === clientId) current.set(goalId, { ...drag, done: true, at: now });
      }
      return { drags: { ...s.drags, [mapId]: Object.fromEntries(current) } };
    }),
  clearDrags: (mapId, predicate) =>
    set((s) => {
      const current = s.drags[mapId];
      if (!current) return s;
      const kept = Object.fromEntries(Object.entries(current).filter(([, drag]) => !predicate(drag)));
      return Object.keys(kept).length === Object.keys(current).length
        ? s
        : { drags: { ...s.drags, [mapId]: kept } };
    }),
  settleDrags: (mapId, userId, revision) =>
    set((s) => {
      const current = s.drags[mapId];
      if (!current || !Object.values(current).some((d) => d.userId === userId)) return s;
      const next = Object.fromEntries(
        Object.entries(current).map(([goalId, drag]) => [
          goalId,
          drag.userId === userId
            ? { ...drag, untilRevision: Math.max(drag.untilRevision ?? 0, revision) }
            : drag,
        ]),
      );
      return { drags: { ...s.drags, [mapId]: next } };
    }),
  setFocus: (mapId, source, focus) =>
    set((s) => {
      const current = s.focus[mapId] ?? {};
      const next = { ...current, [source]: focus ?? undefined };
      return { focus: { ...s.focus, [mapId]: next } };
    }),
}));

/** The focus a heartbeat reports: the title edited on the canvas wins over the open card. */
export function effectiveFocus(sources: { panel?: Focus; canvas?: Focus } | undefined): Focus {
  return sources?.canvas?.goalId ? sources.canvas : (sources?.panel ?? { goalId: null, editing: false });
}

/** Other people's live tabs (expired entries dropped), one entry per tab. */
export function othersOnMap(
  viewers: MapViewer[] | undefined,
  now: number,
  meClientId = CLIENT_ID,
): MapViewer[] {
  return (viewers ?? []).filter((v) => v.clientId !== meClientId && Date.parse(v.expiresAt) > now);
}

/** The people (once each) of `viewers`, the ones typing first. */
export function peopleOf(viewers: MapViewer[]): UserRef[] {
  const seen = new Map<string, UserRef>();
  for (const viewer of [...viewers].sort((a, b) => Number(b.editing) - Number(a.editing)))
    if (!seen.has(viewer.user.id)) seen.set(viewer.user.id, viewer.user);
  return [...seen.values()];
}

/** Who focuses each goal (other tabs only): the people and whether one of them is typing. */
export function focusByGoal(viewers: MapViewer[]): Map<string, { users: UserRef[]; editing: boolean }> {
  const result = new Map<string, { users: UserRef[]; editing: boolean }>();
  for (const viewer of viewers) {
    if (!viewer.goalId) continue;
    const entry = result.get(viewer.goalId) ?? { users: [], editing: false };
    if (!entry.users.some((u) => u.id === viewer.user.id)) entry.users.push(viewer.user);
    entry.editing ||= viewer.editing;
    result.set(viewer.goalId, entry);
  }
  return result;
}

/**
 * Live remote drags of a map: stale ones left out, and dropped ones whose saved move the shown map (`revision`)
 * already reflects.
 */
export function liveDrags(
  drags: Record<string, RemoteDrag> | undefined,
  now: number,
  revision?: number,
): Map<string, RemoteDrag> {
  return new Map(
    Object.entries(drags ?? {}).filter(
      ([, drag]) =>
        now - drag.at < (drag.done ? DROP_STALE_MS : DRAG_STALE_MS) &&
        !(drag.untilRevision !== undefined && revision !== undefined && revision >= drag.untilRevision),
    ),
  );
}
